import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import type { DailyActionType } from 'app/network/daily-stats';
import { navigateToMyInformation } from 'app/screens/billboard/navigator-helper';
import { ListingType } from 'app/types/listing';
import type { SpecificNavigationProp } from 'app/types/navigation';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';
import { IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

interface ContributionMeta {
  emoji: string;
  illustrationBg?:
    | 'surface.brand.secondary-3.default'
    | 'surface.brand.secondary-3.subtle'
    | 'surface.brand.secondary-5.default'
    | 'surface.error.subtle'
    | 'surface.warning.subtle';
  onPress: (navigation: SpecificNavigationProp<'streak'>) => () => void;
}

const CONTRIBUTION_META: Record<DailyActionType, ContributionMeta> = {
  add_avatar: {
    emoji: '📸',
    illustrationBg: 'surface.brand.secondary-3.subtle',
    onPress: (navigation) => () => navigateToMyInformation(navigation),
  },
  add_bio: {
    emoji: '✍️',
    illustrationBg: 'surface.warning.subtle',
    onPress: (navigation) => () => navigateToMyInformation(navigation),
  },
  publish_offer: {
    emoji: '🫴',
    illustrationBg: 'surface.brand.secondary-5.default',
    onPress: (navigation) => () =>
      navigation.navigate(
        'new',
        {
          defaultValues: { type: ListingType.offer },
          newListingSource: 'streak_contribution',
        },
        { pop: true },
      ),
  },
  publish_request: {
    emoji: '👋',
    illustrationBg: 'surface.brand.secondary-5.default',
    onPress: (navigation) => () =>
      navigation.navigate(
        'new',
        {
          defaultValues: { type: ListingType.request },
          newListingSource: 'streak_contribution',
        },
        { pop: true },
      ),
  },
  dismiss_request: {
    emoji: '🙌',
    illustrationBg: 'surface.brand.secondary-5.default',
    onPress: (navigation) => () =>
      navigation.navigate('billboard', { screen: 'all-listings', params: { type: ListingType.request } }),
  },
  send_message: {
    emoji: '💬',
    illustrationBg: 'surface.brand.secondary-3.subtle',
    onPress: (navigation) => () => navigation.navigate('chat', { screen: 'all-channels' }, { pop: true }),
  },
  add_favourite: {
    emoji: '❤️',
    illustrationBg: 'surface.error.subtle',
    onPress: (navigation) => () =>
      navigation.navigate('billboard', { screen: 'all-listings', params: { type: ListingType.offer } }),
  },
};

interface ContributionBottomSheetProps {
  contributionKey: DailyActionType | null;
  navigation: SpecificNavigationProp<'streak'>;
  onClose: () => void;
  onChangeActionType: (actionType: DailyActionType) => void;
}

export const ContributionBottomSheet = function ContributionBottomSheet({
  contributionKey,
  navigation,
  onClose,
  onChangeActionType,
}: ContributionBottomSheetProps) {
  const { t } = useTranslation('streak');

  const handleNavigate = (action: () => void) => {
    onClose();
    setTimeout(action, 250);
  };

  return (
    <BottomSheet onClose={onClose} open={contributionKey !== null} showIndicator={false}>
      <Column padding="spacing.general.x4" gap="spacing.general.x4">
        <Row alignItems="center" justifyContent="space-between">
          <Pressable hitSlop={10} onPress={onClose} style={({ pressed }) => ({ opacity: pressed ? 0.6 : 1 })}>
            <IconX strokeWidth={ICON_STROKE_WIDTH} size={20} />
          </Pressable>
          <Text flex={1} fontSize={16} fontWeight="bold" textAlign="center">
            {contributionKey ? t(`contributions.${contributionKey}.title`) : ''}
          </Text>
          <View style={{ width: 20 }} />
        </Row>

        {contributionKey ? (
          <>
            <ContributionBody
              contributionKey={contributionKey}
              onPrimaryPress={() => {
                const action = CONTRIBUTION_META[contributionKey].onPress(navigation);
                handleNavigate(action);
              }}
            />
            <Button
              marginTop="spacing.general.x2"
              onPress={() => onChangeActionType(contributionKey)}
              variant="secondary"
              style={{ borderRadius: 30, minHeight: 50 }}
            >
              <Text fontSize={15} fontWeight="600">
                {t('skip')}
              </Text>
            </Button>
          </>
        ) : null}
      </Column>
    </BottomSheet>
  );
};

interface ContributionBodyProps {
  contributionKey: DailyActionType;
  onPrimaryPress: () => void;
}

const ContributionBody = function ContributionBody({ contributionKey, onPrimaryPress }: ContributionBodyProps) {
  const { t } = useTranslation('streak');
  const meta = CONTRIBUTION_META[contributionKey];

  return (
    <Column gap="spacing.general.x4">
      <Column
        alignItems="center"
        backgroundColor={meta.illustrationBg ?? 'surface.brand.secondary-3.default'}
        borderRadius="radius.lg"
        height={140}
        justifyContent="center"
      >
        <Text fontSize={56}>{meta.emoji}</Text>
      </Column>
      <Text color="text.placeholder" fontSize={13} textAlign="center">
        {t(`contributions.${contributionKey}.description`)}
      </Text>

      <Button onPress={onPrimaryPress} variant="primary" style={{ borderRadius: 30, minHeight: 50 }}>
        <Text color="white" fontSize={15} fontWeight="600">
          {t(`contributions.${contributionKey}.cta`)}
        </Text>
      </Button>
    </Column>
  );
};
